import {
  mapDeploymentIdsToPlatforms,
  mapTargetCompanySizeIdsToCustomers,
  mapTargetCompanySizeIdsToNames,
} from 'helpers/bxApiMappings';
import formatData from 'helpers/dataTransformers/pricing';
import { getBxResearchBoardProductCanonicalId } from 'helpers/researchBoardProduct';

// --- Helpers ---

const toRelativePath = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url, 'https://www.getapp.com');

    // Normalize URLs that resolve to the getapp.com origin to a relative path
    if (parsed.origin === 'https://www.getapp.com') {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }

    // For non-getapp origins, return the original URL (likely an external link)
    return url;
  } catch (e) {
    // If URL parsing fails (e.g., malformed URL), fall back to the original string
    return url;
  }
};

// --- Array-style filter helpers ---

const toArray = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((v) => String(v).split(','));
  }
  if (value) {
    return String(value).split(',');
  }
  return [];
};

// Maps URL query param keys to their corresponding facet object keys
const QUERY_PARAM_TO_FACET_KEY = {
  'pricingcategories_slug[]': 'pricingcategories_facet',
  pricingcategories_slug: 'pricingcategories_facet',
  'oses_slug[]': 'oses_facet',
  oses_slug: 'oses_facet',
  'tag_slug[]': 'feature_tags_facet',
  tag_slug: 'feature_tags_facet',
  'integrated_with_slug[]': 'integrated_with_facet',
  integrated_with_slug: 'integrated_with_facet',
  'typecustomers_slug[]': 'typecustomers_facet',
  typecustomers_slug: 'typecustomers_facet',
  rating_stars_ge: 'rating_stars_ge',
  community_company_size: 'community_company_size',
  community_company_industry: 'community_company_industry',
};

// Inverse map: facet key → canonical param key used in the URL
const FACET_KEY_TO_PARAM = {
  pricingcategories_facet: 'pricingcategories_slug[]',
  oses_facet: 'oses_slug[]',
  feature_tags_facet: 'tag_slug[]',
  integrated_with_facet: 'integrated_with_slug[]',
  typecustomers_facet: 'typecustomers_slug[]',
  rating_stars_ge: 'rating_stars_ge',
  community_company_size: 'community_company_size',
  community_company_industry: 'community_company_industry',
};

// Builds the URL that adds a specific filter value to the current query params.
const buildSelectHref = (baseUrl, query, facetKey, itemValue) => {
  const paramKey = FACET_KEY_TO_PARAM[facetKey];
  if (!paramKey) return baseUrl;
  const bareKey = paramKey.replace('[]', '');
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, val]) => {
    toArray(val).forEach((v) => {
      params.append(key, v);
    });
  });
  // Add the new filter value
  params.append(bareKey, String(itemValue));
  const qs = params.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
};

// Builds the URL that removes a specific filter value from the current query params.
const buildDeselectHref = (baseUrl, query, facetKey, itemValue) => {
  const paramKey = FACET_KEY_TO_PARAM[facetKey];
  if (!paramKey) return baseUrl;
  const bareKey = paramKey.replace('[]', '');
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, val]) => {
    const isThisParam = key === paramKey || key === bareKey;
    toArray(val).forEach((v) => {
      if (isThisParam && v === String(itemValue)) return;
      params.append(key, v);
    });
  });
  const qs = params.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
};

// --- Profile URL builder ---

const buildProfileUrl = (slug, categoryNodes, fallbackCategorySlug) => {
  const catSlug =
    (categoryNodes || []).filter(Boolean)[0]?.slug || fallbackCategorySlug;
  return catSlug ? `/${catSlug}-software/a/${slug}/` : `/${slug}/`;
};

// --- Pricing ---

const transformPricing = (product) => {
  if (!product.pricing) return null;
  return formatData(product);
};

// --- Review dimension ratings ---

const transformByField = (reviewSummary) => ({
  rating_value: reviewSummary?.valueForMoneyRating?.toFixed(2) || '0',
  rating_features: reviewSummary?.functionalityRating?.toFixed(2) || '0',
  rating_ease: reviewSummary?.easeOfUseRating?.toFixed(2) || '0',
  rating_support: reviewSummary?.customerSupportRating?.toFixed(2) || '0',
  likelihood_to_recommend:
    reviewSummary?.likelihoodToRecommend?.toFixed(2) || '0',
});

// --- Rating breakdown ---

const transformByRating = (breakdown) => {
  const byRating = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const entry of breakdown || []) {
    if (entry && entry.value >= 1 && entry.value <= 5) {
      byRating[entry.value] = entry.count || 0;
    }
  }
  return byRating;
};

// --- Top features (sorted by quality rating) ---

const transformTopFeatures = (featureNodes) =>
  (featureNodes || [])
    .filter((f) => f.ratingSummary?.qualityRatingCount > 0)
    .sort(
      (a, b) =>
        (b.ratingSummary?.qualityRating || 0) -
        (a.ratingSummary?.qualityRating || 0)
    )
    .slice(0, 3)
    .map((f) => ({
      id: f.id,
      name: f.name,
      avg_rating: f.ratingSummary?.qualityRating || 0,
      cnt_rating: f.ratingSummary?.qualityRatingCount || 0,
    }));

// --- Feature comparison between main listing and alternative ---

const computeFeatureComparison = (mainFeatureNodes, altFeatureNodes) => {
  const mainIds = new Set((mainFeatureNodes || []).map((f) => f.id));
  const altIds = new Set((altFeatureNodes || []).map((f) => f.id));

  const mainNameById = {};
  for (const f of mainFeatureNodes || []) mainNameById[f.id] = f.name;
  const altNameById = {};
  for (const f of altFeatureNodes || []) altNameById[f.id] = f.name;

  let commonCount = 0;
  for (const id of mainIds) {
    if (altIds.has(id)) commonCount++;
  }

  const uniqueMain = [];
  for (const id of mainIds) {
    if (!altIds.has(id)) uniqueMain.push(mainNameById[id]);
  }

  const uniqueAlt = [];
  for (const id of altIds) {
    if (!mainIds.has(id)) uniqueAlt.push(altNameById[id]);
  }

  return { commonCount, uniqueMain, uniqueAlt };
};

// --- Snippets ---

const transformSnippets = (snippetNodes) =>
  (snippetNodes || []).filter(Boolean).map((node) => ({
    text: node.text,
    type:
      String(node.sentiment || '').toLowerCase() === 'negative'
        ? 'cons'
        : 'pros',
    review_id: node.textReview?.id,
    reviewer: node.textReview?.reviewer
      ? {
          user_name: [
            node.textReview.reviewer.firstName,
            node.textReview.reviewer.lastName,
          ]
            .filter(Boolean)
            .join(' '),
          job_title: node.textReview.reviewer.jobTitle || '',
          avatar: node.textReview.reviewer.profilePictureUrl || null,
          is_validated: node.textReview.reviewer.isValidated || false,
        }
      : null,
  }));

// --- Facets ---

const PRICING_CATEGORY_LABELS = {
  free: 'Free',
  'free-trial': 'Free Trial',
  subscription: 'Subscription',
  'one-time-license': 'One-time License',
  'open-source': 'Open Source',
};

const RATING_STAR_THRESHOLDS = [4, 3, 2];

const COMMUNITY_COMPANY_SIZES = [
  'myself only',
  '2-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+',
];

const buildFacets = (alternatives, basePath) => {
  // Pricing categories facet
  const pricingCounts = {};
  for (const alt of alternatives) {
    for (const cat of alt.pricing?.categories || []) {
      pricingCounts[cat] = (pricingCounts[cat] || 0) + 1;
    }
  }
  const pricingcategories_facet = Object.entries(PRICING_CATEGORY_LABELS)
    .filter(([value]) => pricingCounts[value] > 0)
    .map(([value, label]) => ({
      value,
      label,
      selected: false,
      href: `${basePath}?pricingcategories_slug=${value}`,
      count: pricingCounts[value] || 0,
      is_seo: false,
    }));

  // Feature tags facet — top features across all alternatives
  const featureCounts = {};
  const featureLabels = {};
  for (const alt of alternatives) {
    const seen = new Set();
    for (const f of alt.facetFeatureNodes || []) {
      if (!seen.has(f.id)) {
        seen.add(f.id);
        featureCounts[f.id] = (featureCounts[f.id] || 0) + 1;
        featureLabels[f.id] = f.name;
      }
    }
  }
  const feature_tags_facet = Object.entries(featureCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([id, count]) => ({
      value: id,
      label: featureLabels[id],
      selected: false,
      href: `${basePath}?tag_slug=${id}`,
      count,
      is_seo: false,
    }));

  // Rating stars facet
  const rating_stars_ge = [
    {
      value: 'all',
      label: 'All Reviews',
      selected: true,
      href: basePath,
      is_seo: false,
      count: null,
    },
    ...RATING_STAR_THRESHOLDS.map((threshold) => ({
      value: threshold,
      label: null,
      selected: false,
      href: `${basePath}?rating_stars_ge=${threshold}`,
      is_seo: false,
      count: null,
    })),
  ];

  // Customer type facet
  const customerCounts = {};
  const customerLabels = {};
  for (const alt of alternatives) {
    const seen = new Set();
    for (const c of alt.facetCustomers || []) {
      if (!seen.has(c.slug)) {
        seen.add(c.slug);
        customerCounts[c.slug] = (customerCounts[c.slug] || 0) + 1;
        customerLabels[c.slug] = c.name;
      }
    }
  }
  const typecustomers_facet = Object.entries(customerCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([slug, count]) => ({
      value: slug,
      label: customerLabels[slug],
      selected: false,
      href: `${basePath}?typecustomers_slug=${slug}`,
      count,
      is_seo: false,
    }));

  // OS / deployment facet
  const osCounts = {};
  const osLabels = {};
  for (const alt of alternatives) {
    const seen = new Set();
    for (const p of alt.facetPlatforms || []) {
      if (!seen.has(p.slug)) {
        seen.add(p.slug);
        osCounts[p.slug] = (osCounts[p.slug] || 0) + 1;
        osLabels[p.slug] = p.name;
      }
    }
  }
  const oses_facet = Object.entries(osCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([slug, count]) => ({
      value: slug,
      label: osLabels[slug],
      selected: false,
      href: `${basePath}?oses_slug=${slug}`,
      count,
      is_seo: false,
    }));

  // Integrated with facet
  const integrationCounts = {};
  const integrationLabels = {};
  for (const alt of alternatives) {
    const seen = new Set();
    for (const intg of alt.facetIntegrations || []) {
      if (!seen.has(intg.slug)) {
        seen.add(intg.slug);
        integrationCounts[intg.slug] = (integrationCounts[intg.slug] || 0) + 1;
        integrationLabels[intg.slug] = intg.name;
      }
    }
  }
  const integrated_with_facet = Object.entries(integrationCounts)
    .sort(([a], [b]) =>
      (integrationLabels[a] || '').localeCompare(integrationLabels[b] || '')
    )
    .map(([slug, count]) => ({
      value: slug,
      label: integrationLabels[slug],
      selected: false,
      href: `${basePath}?integrated_with_slug=${slug}`,
      count,
      is_seo: false,
    }));

  // Community company industry facet (dynamic from targetIndustries)
  const industryCounts = {};
  for (const alt of alternatives) {
    const seen = new Set();
    for (const ind of alt.facetIndustries || []) {
      if (!seen.has(ind.name)) {
        seen.add(ind.name);
        industryCounts[ind.name] = (industryCounts[ind.name] || 0) + 1;
      }
    }
  }
  const community_company_industry = Object.entries(industryCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, count]) => ({
      value: name,
      label: name,
      selected: false,
      href: `${basePath}?community_company_industry=${encodeURIComponent(name)}`,
      count,
      is_seo: false,
    }));

  // Community company size facet (static reviewer demographics)
  const community_company_size = COMMUNITY_COMPANY_SIZES.map((label) => ({
    value: label,
    label,
    selected: false,
    href: `${basePath}?community_company_size=${encodeURIComponent(label)}`,
    count: null,
    is_seo: false,
  }));

  // Order matches backend facet rendering order
  return {
    feature_tags_facet,
    pricingcategories_facet,
    oses_facet,
    rating_stars_ge,
    typecustomers_facet,
    integrated_with_facet,
    community_company_industry,
    community_company_size,
  };
};

// --- Client-side filtering ---

export const filterAlternatives = (alternatives, filters) => {
  if (!filters || Object.keys(filters).length === 0) return alternatives;

  return alternatives.filter((alt) => {
    // Pricing category filter (supports multiple values)
    if (filters.pricingcategories_slug) {
      const filterValues = toArray(filters.pricingcategories_slug);
      const cats = alt.pricing?.categories || [];
      if (!filterValues.some((val) => cats.includes(val))) return false;
    }

    // Feature tag filter (supports multiple values)
    if (filters.tag_slug) {
      const filterValues = toArray(filters.tag_slug);
      const featureIds = (alt.facetFeatureNodes || []).map((f) => f.id);
      if (!filterValues.some((val) => featureIds.includes(val))) return false;
    }

    // Rating stars filter
    if (filters.rating_stars_ge) {
      const threshold = Number(filters.rating_stars_ge);
      if (!isNaN(threshold) && (alt.rating_avg || 0) < threshold) return false;
    }

    // Customer type filter (supports multiple values)
    if (filters.typecustomers_slug) {
      const filterValues = toArray(filters.typecustomers_slug);
      const customerSlugs = (alt.facetCustomers || []).map((c) => c.slug);
      if (!filterValues.some((val) => customerSlugs.includes(val)))
        return false;
    }

    // OS filter (supports multiple values)
    if (filters.oses_slug) {
      const filterValues = toArray(filters.oses_slug);
      const platformSlugs = (alt.facetPlatforms || []).map((p) => p.slug);
      if (!filterValues.some((val) => platformSlugs.includes(val)))
        return false;
    }

    // Integration filter (supports multiple values)
    if (filters.integrated_with_slug) {
      const filterValues = toArray(filters.integrated_with_slug);
      const integrationSlugs = (alt.facetIntegrations || []).map((i) => i.slug);
      if (!filterValues.some((val) => integrationSlugs.includes(val)))
        return false;
    }

    // Company size filter
    if (filters.community_company_size) {
      if (
        !(alt.facetCompanySizes || []).includes(filters.community_company_size)
      )
        return false;
    }

    // Industry filter
    if (filters.community_company_industry) {
      const industryNames = (alt.facetIndustries || []).map((ind) => ind.name);
      if (!industryNames.includes(filters.community_company_industry))
        return false;
    }

    return true;
  });
};

export const updateFacetCounts = (facets, filteredAlternatives) => {
  const updated = {};
  for (const [facetKey, facetItems] of Object.entries(facets)) {
    updated[facetKey] = facetItems.map((item) => {
      // Static facets keep count as null (matching backend behavior)
      if (facetKey === 'rating_stars_ge') return { ...item };

      let count = 0;
      for (const alt of filteredAlternatives) {
        if (facetKey === 'pricingcategories_facet') {
          if ((alt.pricing?.categories || []).includes(item.value)) count++;
        } else if (facetKey === 'feature_tags_facet') {
          if (
            (alt.facetFeatureNodes || []).map((f) => f.id).includes(item.value)
          )
            count++;
        } else if (facetKey === 'typecustomers_facet') {
          if (
            (alt.facetCustomers || []).map((c) => c.slug).includes(item.value)
          )
            count++;
        } else if (facetKey === 'oses_facet') {
          if (
            (alt.facetPlatforms || []).map((p) => p.slug).includes(item.value)
          )
            count++;
        } else if (facetKey === 'integrated_with_facet') {
          if (
            (alt.facetIntegrations || [])
              .map((i) => i.slug)
              .includes(item.value)
          )
            count++;
        } else if (facetKey === 'community_company_size') {
          if ((alt.facetCompanySizes || []).includes(item.value)) count++;
        } else if (facetKey === 'community_company_industry') {
          if (
            (alt.facetIndustries || [])
              .map((ind) => ind.name)
              .includes(item.value)
          )
            count++;
        }
      }
      return { ...item, count };
    });
  }
  return updated;
};

// ============================================================
// Main public API
// ============================================================

export const transformAlternativesData = (
  bxProduct,
  basePath,
  categoryHeader
) => {
  if (!bxProduct) return { listing: null, alternatives: [], facets: {} };

  const mainFeatureNodes = (bxProduct.features?.nodes || []).filter(Boolean);
  const mainCatNodes = (bxProduct.categories?.nodes || []).filter(Boolean);
  // Main listing uses the page's category context (from URL) to match user's current view
  const mainProfileUrl = categoryHeader
    ? `/${categoryHeader}-software/a/${bxProduct.slug}/`
    : buildProfileUrl(bxProduct.slug, mainCatNodes, null);

  // --- Transform main listing ---
  const mainPricing = transformPricing(bxProduct);
  const listing = {
    id: bxProduct.seoId || bxProduct.id,
    global_product_id: bxProduct.id,
    product_canonical_id: getBxResearchBoardProductCanonicalId(bxProduct),
    name: bxProduct.name,
    slug: bxProduct.slug,
    logo_url: bxProduct.logoUrl,
    tagline: bxProduct.tagline,
    company_name: bxProduct.vendor?.name,
    rating_avg: bxProduct.reviewSummary?.overallRating || 0,
    num_reviews: bxProduct.reviewSummary?.totalReviews || 0,
    is_premium: !!bxProduct.campaign?.id,
    last_updated_on: bxProduct.modifiedOn,
    cta_urls: {
      cta: bxProduct.campaign?.destinationUrl || mainProfileUrl,
      visit_website: bxProduct.campaign?.destinationUrl || mainProfileUrl,
      detail: mainProfileUrl,
      reviews: null,
    },
    by_field: transformByField(bxProduct.reviewSummary),
    pricing: mainPricing,
    features: {
      total_count: bxProduct.features?.totalCount || 0,
    },
    top_features: transformTopFeatures(mainFeatureNodes),
    reviews: {
      by_rating: transformByRating(
        bxProduct.reviewSummary?.overallRatingBreakdown
      ),
    },
    summary: {
      top: [], // filled below after processing alternatives
    },
    platforms: mapDeploymentIdsToPlatforms(bxProduct.deploymentOptions),
    categories: mainCatNodes.map((c) => ({
      id: c.id,
      name: c.name,
      slug: c.slug,
    })),
  };

  // --- Transform alternatives ---
  const altNodes = (bxProduct.relatedProducts?.nodes || []).filter(Boolean);
  const alternatives = altNodes.map((alt) => {
    const altFeatureNodes = (alt.features?.nodes || []).filter(Boolean);
    const altCatNodes = (alt.categories?.nodes || []).filter(Boolean);
    const { commonCount, uniqueMain, uniqueAlt } = computeFeatureComparison(
      mainFeatureNodes,
      altFeatureNodes
    );
    const altPricing = transformPricing(alt);
    const altProfileUrl = toRelativePath(alt?.url);
    const platforms = mapDeploymentIdsToPlatforms(alt.deploymentOptions);
    const customers = mapTargetCompanySizeIdsToCustomers(
      alt.targetCompanySizes
    );
    const companySizes = mapTargetCompanySizeIdsToNames(alt.targetCompanySizes);
    const integrations = (alt.integrations?.nodes || []).filter(Boolean);

    return {
      id: alt.seoId || alt.id,
      global_product_id: alt.id,
      product_canonical_id: getBxResearchBoardProductCanonicalId(alt),
      name: alt.name,
      slug: alt.slug,
      logo_url: alt.logoUrl,
      tagline: alt.tagline,
      company_name: alt.vendor?.name,
      rating_avg: alt.reviewSummary?.overallRating || 0,
      num_reviews: alt.reviewSummary?.totalReviews || 0,
      is_premium: !!alt.campaign?.id,
      cta_urls: {
        cta: alt.campaign?.destinationUrl || altProfileUrl,
        detail: altProfileUrl,
      },
      by_field: transformByField(alt.reviewSummary),
      pricing: altPricing,
      features: {
        total_count: alt.features?.totalCount || 0,
        common_count: commonCount,
        unique: {
          main: uniqueMain,
          alt: uniqueAlt,
        },
      },
      top_features: transformTopFeatures(altFeatureNodes),
      snippets: transformSnippets(alt.reviewSnippets?.nodes),
      categories: altCatNodes.map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
      })),
      // Internal fields for facet building and client-side filtering
      facetFeatureNodes: altFeatureNodes,
      facetPlatforms: platforms,
      facetCustomers: customers,
      facetCompanySizes: companySizes,
      facetIntegrations: integrations,
      facetIndustries: (alt.targetIndustries || []).filter(Boolean),
    };
  });

  // --- Free alternatives sidebar ---
  listing.summary.top = alternatives
    .filter((alt) => alt.pricing?.categories?.includes('free'))
    .slice(0, 10)
    .map((alt) => ({
      app_name: alt.name,
      href: alt.cta_urls.detail,
      global_product_id: alt.global_product_id,
    }));

  // --- Build facets ---
  const facets = buildFacets(
    alternatives,
    basePath || `/${bxProduct.slug}/alternatives/`
  );

  return { listing, alternatives, facets };
};

// Mark facet items as selected based on active URL query params and fix their hrefs.
// Needed because facets are generated without knowing which filters are active,
// so we need to update them based on the URL query params.
export const applySelectedFacets = (facets, query, baseUrl) => {
  const selectedByFacetKey = {};
  Object.entries(QUERY_PARAM_TO_FACET_KEY).forEach(([param, facetKey]) => {
    const values = toArray(query[param]);
    if (values.length) {
      selectedByFacetKey[facetKey] = new Set([
        ...(selectedByFacetKey[facetKey] || []),
        ...values,
      ]);
    }
  });

  return Object.fromEntries(
    Object.entries(facets).map(([key, items]) => [
      key,
      items.map((item) => {
        const isSelected =
          selectedByFacetKey[key]?.has(String(item.value)) || false;
        return {
          ...item,
          selected: isSelected,
          href: isSelected
            ? buildDeselectHref(baseUrl, query, key, item.value)
            : buildSelectHref(baseUrl, query, key, item.value),
        };
      }),
    ])
  );
};
